import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const FIELDS = ["brand", "model", "length", "beam", "dry_weight", "fuel_capacity", "draft", "max_hp", "passenger_capacity", "live_well_capacity", "length_on_trailer"];
const BOAT_TYPES = ["Bass Boat", "Bay Boat", "Pontoon", "Center Console", "Deck Boat", "Sport", "Other"];

export default function ModelSpecs() {
  const [specs, setSpecs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [genning, setGenning] = useState(false);

  const genAboutModel = async () => {
    if (!form.brand || !form.model) { toast.error("Enter the brand and model first"); return; }
    setGenning(true);
    try { const r = await api.post("/admin/model-specs/generate-about", form); setForm((f) => ({ ...f, about_model: r.data.about_model })); toast.success("Draft generated, review and edit as needed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGenning(false); }
  };

  const load = useCallback(() => { api.get("/model-specs").then((r) => setSpecs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ brand: "", model: "" }); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm(s); setOpen(true); };

  const save = async () => {
    if (!form.brand || !form.model) { toast.error("Brand and Model required"); return; }
    try {
      if (editing) await api.put(`/admin/model-specs/${editing.id}`, form);
      else await api.post("/admin/model-specs", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this model spec?")) return; await api.delete(`/admin/model-specs/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-models">
      <AdminHeader title="Brands / Models" subtitle="Model specification library, stored once, auto-fills new inventory"
        action={<Button onClick={openNew} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="add-model-spec-button"><Plus size={16} /> Add Model Spec</Button>} />
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono"><tr><th className="text-left px-4 py-3">Brand</th><th className="text-left px-4 py-3">Model</th><th className="text-left px-4 py-3 hidden md:table-cell">Length</th><th className="text-left px-4 py-3 hidden md:table-cell">Max HP</th><th className="px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {specs.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50" data-testid={`model-spec-${s.id}`}>
                <td className="px-4 py-3 font-semibold text-slate-900">{s.brand}</td>
                <td className="px-4 py-3">{s.model}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{s.length}</td>
                <td className="px-4 py-3 hidden md:table-cell text-slate-500">{s.max_hp}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-spec-${s.id}`}>Edit</Button>
                  <Button variant="ghost" size="icon" onClick={() => del(s.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></Button>
                </td>
              </tr>
            ))}
            {!specs.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No model specs yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="model-spec-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Model Spec" : "Add Model Spec"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Boat Type (category)</Label>
              <Select value={form.boat_type || ""} onValueChange={(v) => setForm({ ...form, boat_type: v })}>
                <SelectTrigger data-testid="spec-boat_type"><SelectValue placeholder="Select type (auto-fills every listing of this model)" /></SelectTrigger>
                <SelectContent>{BOAT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {FIELDS.map((f) => (
              <div key={f}><Label className="text-xs capitalize">{f.replace(/_/g, " ")}</Label><Input value={form[f] ?? ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`spec-${f}`} /></div>
            ))}
            <div className="col-span-2"><Label className="text-xs">Standard Features</Label><Textarea rows={2} value={form.standard_features ?? ""} onChange={(e) => setForm({ ...form, standard_features: e.target.value })} /></div>
            <div className="col-span-2"><Label className="text-xs">Trailer Specs</Label><Textarea rows={2} value={form.standard_specs ?? ""} onChange={(e) => setForm({ ...form, standard_specs: e.target.value })} /></div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">About This Model (SEO)</Label>
                <Button type="button" variant="outline" size="sm" onClick={genAboutModel} disabled={genning} className="rounded-full h-7 text-xs" data-testid="generate-about-button">{genning ? "Generating..." : "Generate SEO Draft"}</Button>
              </div>
              <Textarea rows={7} value={form.about_model ?? ""} onChange={(e) => setForm({ ...form, about_model: e.target.value })} placeholder="Two paragraphs about what this model is best for. Shown on every listing of this model." data-testid="spec-about_model" />
            </div>
          </div>
          <Button onClick={save} className="bg-mtm-purple text-white rounded-full" data-testid="save-spec-button"><Save size={15} /> Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
